import { useParams, Link, useNavigate } from 'react-router-dom';
import { movies, movieCategories, Movie } from '@/mocks/movies';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';

function getCategoryName(categoryId: string): string {
  const cat = movieCategories.find((c) => c.id === categoryId);
  return cat ? cat.name : '未知';
}

function RelatedMovies({ currentMovie }: { currentMovie: Movie }) {
  const related = movies
    .filter((m) => m.id !== currentMovie.id && m.category === currentMovie.category)
    .slice(0, 4);

  if (related.length === 0) return null;

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/50">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6">
          相关推荐
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {related.map((movie) => (
            <Link
              key={movie.id}
              to={`/article/${movie.id}`}
              className="group block cursor-pointer"
            >
              <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-3">
                <img
                  src={movie.image}
                  alt={movie.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm rounded-md px-2 py-0.5 text-accent-400 text-xs font-semibold whitespace-nowrap">
                  {movie.rating}
                </div>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                {movie.title}
              </h3>
              <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">{movie.year}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Article() {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const movie = movies.find((m) => m.id === articleId);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-20 h-20 flex items-center justify-center text-foreground-300 mb-4">
            <i className="ri-error-warning-line text-5xl"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">电影未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">抱歉，您查看的电影不存在或已下架</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryName = getCategoryName(movie.category);
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '电影分类', href: '/category/all' },
    { label: categoryName, href: `/category/${movie.category}` },
    { label: movie.title },
  ];

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          headline: movie.title,
          description: movie.description,
          author: { '@type': 'Person', name: movie.director },
          datePublished: movie.releaseDate,
          image: movie.image,
          publisher: { '@type': 'Organization', name: '萝卜影视' },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://luobo-movies.com/' },
            { '@type': 'ListItem', position: 2, name: '电影分类', item: 'https://luobo-movies.com/category/all' },
            { '@type': 'ListItem', position: 3, name: categoryName, item: `https://luobo-movies.com/category/${movie.category}` },
            { '@type': 'ListItem', position: 4, name: movie.title },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <article className="w-full px-4 md:px-6 py-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 mb-10">
                <div className="w-full lg:w-72 flex-shrink-0">
                  <div className="rounded-lg overflow-hidden aspect-[2/3] lg:sticky lg:top-24">
                    <img
                      src={movie.image}
                      alt={movie.title}
                      title={`${movie.title} - 萝卜影视高清在线观看`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                    {movie.title}
                  </h1>

                  <div className="flex items-center gap-4 flex-wrap mb-6">
                    <div className="flex items-center gap-1.5">
                      <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                        <i className="ri-star-fill"></i>
                      </span>
                      <span className="text-lg font-bold text-accent-600 whitespace-nowrap">{movie.rating}</span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">/ 10</span>
                    </div>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-600 whitespace-nowrap">{movie.year}</span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-600 whitespace-nowrap">{movie.duration}</span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-600 whitespace-nowrap">{movie.region}</span>
                  </div>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-3">
                      <span className="text-sm font-medium text-foreground-400 whitespace-nowrap min-w-[3.5rem]">导演</span>
                      <span className="text-sm text-foreground-800">{movie.director}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-sm font-medium text-foreground-400 whitespace-nowrap min-w-[3.5rem]">主演</span>
                      <span className="text-sm text-foreground-800">{movie.actors.join(' / ')}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-sm font-medium text-foreground-400 whitespace-nowrap min-w-[3.5rem]">语言</span>
                      <span className="text-sm text-foreground-800">{movie.language}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-sm font-medium text-foreground-400 whitespace-nowrap min-w-[3.5rem]">上映</span>
                      <span className="text-sm text-foreground-800">{movie.releaseDate}</span>
                    </div>
                  </div>

                  <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                    {movie.description}
                  </p>

                  <Link
                    to={`/category/${movie.category}`}
                    className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line text-xs"></i>
                    </span>
                    返回{categoryName}分类
                  </Link>
                </div>
              </div>

              <div className="border-t border-background-200/70 pt-8">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">剧情简介</h2>
                <div className="text-sm text-foreground-700 leading-relaxed space-y-3">
                  {movie.content.split('\n').map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </div>
            </div>
          </article>

          <RelatedMovies currentMovie={movie} />
        </main>
        <Footer />
      </div>
    </>
  );
}